import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { Paginated } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { EmptyState, ErrorState } from './states';
import type { TableParams } from './use-table-params';
import { Skeleton } from '@/components/ui/skeleton';

export type DataColumn<T> = ColumnDef<T, unknown> & { meta?: { sortKey?: string; align?: 'right' | 'left'; className?: string } };

/**
 * Tabela com paginação, ordenação e busca no servidor. Estado na URL via `params` (nuqs).
 * `filters` aparece ao lado da busca (selects de domínio).
 */
export function DataTable<T>({
  columns,
  data,
  params,
  isLoading,
  error,
  onRetry,
  searchPlaceholder = 'Buscar',
  filters,
  toolbar,
  empty,
  onRowClick,
}: {
  columns: DataColumn<T>[];
  data: Paginated<T> | undefined;
  params: TableParams;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  searchPlaceholder?: string;
  filters?: ReactNode;
  toolbar?: ReactNode;
  empty?: { title: string; description?: string; action?: ReactNode; image?: string };
  onRowClick?: (row: T) => void;
}) {
  const [search, setSearch] = useState(params.q);
  useEffect(() => setSearch(params.q), [params.q]);
  useEffect(() => {
    const t = setTimeout(() => search !== params.q && params.setSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({ data: data?.data ?? [], columns, getCoreRowModel: getCoreRowModel(), manualPagination: true, manualSorting: true });
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / params.pageSize));

  return (
    <div className="grid gap-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="relative w-full md:max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} className="pl-8" />
        </div>
        {filters && <div className="flex flex-wrap items-center gap-2">{filters}</div>}
        {toolbar && <div className="flex flex-wrap items-center gap-2 md:ml-auto">{toolbar}</div>}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="hover:bg-transparent">
                  {hg.headers.map((h) => {
                    const meta = (h.column.columnDef as DataColumn<T>).meta;
                    const sortKey = meta?.sortKey;
                    const active = sortKey && params.sort === sortKey;
                    return (
                      <TableHead
                        key={h.id}
                        className={cn('h-10 text-xs font-semibold text-muted-foreground', meta?.align === 'right' && 'text-right', meta?.className)}
                        aria-sort={active ? (params.order === 'asc' ? 'ascending' : 'descending') : undefined}
                      >
                        {sortKey ? (
                          <button
                            type="button"
                            onClick={() => params.toggleSort(sortKey)}
                            className={cn('inline-flex items-center gap-1 hover:text-foreground', active && 'text-foreground')}
                          >
                            {flexRender(h.column.columnDef.header, h.getContext())}
                            {active ? (
                              params.order === 'asc' ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />
                            ) : (
                              <ArrowUpDown className="size-3.5 opacity-40" />
                            )}
                          </button>
                        ) : (
                          flexRender(h.column.columnDef.header, h.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`s${i}`}>
                    {columns.map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full max-w-40" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!isLoading &&
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={cn(onRowClick && 'cursor-pointer')}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = (cell.column.columnDef as DataColumn<T>).meta;
                      return (
                        <TableCell key={cell.id} className={cn(meta?.align === 'right' && 'text-right tabular', meta?.className)}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!isLoading && total === 0 && (
            <div className="p-4">
              <EmptyState
                title={params.q ? 'Nenhum resultado para esta busca' : (empty?.title ?? 'Nada por aqui ainda')}
                description={params.q ? 'Revise o termo ou limpe os filtros.' : empty?.description}
                action={params.q ? undefined : empty?.action}
                image={params.q ? undefined : empty?.image}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col items-center justify-between gap-2 text-sm text-muted-foreground sm:flex-row">
        <div className="flex items-center gap-2">
          <span>Linhas por página</span>
          <Select value={String(params.pageSize)} onValueChange={(v) => params.set({ pageSize: Number(v), page: 1 })}>
            <SelectTrigger size="sm" className="w-20" aria-label="Linhas por página">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="tabular">{total} no total</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" aria-label="Página anterior" disabled={params.page <= 1} onClick={() => params.set({ page: params.page - 1 })}>
            <ChevronLeft className="size-4" />
          </Button>
          <span className="tabular px-2">
            {params.page} de {pages}
          </span>
          <Button variant="ghost" size="icon" aria-label="Próxima página" disabled={params.page >= pages} onClick={() => params.set({ page: params.page + 1 })}>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
